// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Le titulaire d'un Cabinet nomme son cabinet — fenêtre qu'on ne peut pas fermer.
 *
 * ⚠️ BLOQUANTE À DESSEIN (décision du 15/09/2026). Le nom est ce que voient ses
 * collègues dans leur barre latérale et dans l'invitation qu'ils reçoivent ; une
 * licence Cabinet naît d'un paiement, sans nom. La fenêtre s'ouvre pour le seul
 * titulaire (`est_titulaire_cabinet`), tant que `nom_a_definir` est vrai — y
 * compris quand on arrive à Cabinet par un changement de palier, qui ne passe
 * jamais par la page de paiement Stripe. Le serveur refuse de toute façon
 * d'inviter tant que le nom manque : la fenêtre n'est pas la serrure, seulement
 * le chemin le plus court vers la clé.
 */
const NommerCabinetModal = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [nom, setNom] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const licence = user?.license;
  if (!licence?.est_titulaire_cabinet || !licence?.nom_a_definir) return null;

  const enregistrer = async (e) => {
    e.preventDefault();
    setErreur('');
    setEnvoi(true);
    try {
      const r = await fetch(`${API}/api/license/identite`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErreur(d.error || t('err_generic')); setEnvoi(false); return; }
      await refreshUser();
    } catch (_) {
      setErreur(t('err_network'));
      setEnvoi(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="nommer-cabinet-titre"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={enregistrer}
        style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div id="nommer-cabinet-titre" style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{t('cabinet_nom_titre')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>{t('cabinet_nom_texte')}</div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('cabinet_nom_label')}</label>
        <input type="text" value={nom} onChange={e => setNom(e.target.value)} autoFocus maxLength={80}
          placeholder={t('cabinet_nom_placeholder')}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        {erreur && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{erreur}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="submit" disabled={envoi || nom.trim().length < 2}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (envoi || nom.trim().length < 2) ? 0.6 : 1 }}>
            {envoi ? '…' : t('cabinet_nom_valider')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NommerCabinetModal;
